import type { LvdProductComplianceFileView } from '@/shared/hooks/api/lvdComplianceFiles'
import { parseConfirmedLvdStandards } from '@/v2/utils/lvdLegislationBriefing'

export const LVD_PRODUCT_COMPLIANCE_FILE_DECLARATION_OF_CONFORMITY_REQUIREMENT_KEYS =
  [
    'manufacturerIdentity',
    'declarationObject',
    'confirmedStandards',
    'productExportable',
  ] as const

export type LvdProductComplianceFileDeclarationOfConformityRequirementKey =
  (typeof LVD_PRODUCT_COMPLIANCE_FILE_DECLARATION_OF_CONFORMITY_REQUIREMENT_KEYS)[number]

export type LvdProductComplianceFileDeclarationOfConformityRequirement = {
  key: LvdProductComplianceFileDeclarationOfConformityRequirementKey
  met: boolean
}

type EvaluateInput = {
  productName?: string | null
  productSku?: string | null
  productDescription?: string | null
  lvdApplies: boolean
  productFile: LvdProductComplianceFileView | null
  manufacturerName?: string | null
  manufacturerAddress?: string | null
}

function isProductExportable(input: EvaluateInput): boolean {
  if (!input.lvdApplies) {
    return false
  }
  if (input.productFile?.status !== 'ACTIVE') {
    return false
  }
  return true
}

export function evaluateLvdProductComplianceFileDeclarationOfConformityRequirements(
  input: EvaluateInput
): {
  requirements: LvdProductComplianceFileDeclarationOfConformityRequirement[]
  allMet: boolean
  generateReady: boolean
  unmetKeys: LvdProductComplianceFileDeclarationOfConformityRequirementKey[]
  unmetKeysExceptManufacturer: LvdProductComplianceFileDeclarationOfConformityRequirementKey[]
} {
  const manufacturerMet =
    Boolean(input.manufacturerName?.trim()) &&
    Boolean(input.manufacturerAddress?.trim())
  const declarationObjectMet =
    Boolean(input.productName?.trim()) &&
    Boolean(input.productSku?.trim()) &&
    Boolean(input.productDescription?.trim())
  const confirmedStandardsMet =
    parseConfirmedLvdStandards(
      input.productFile?.confirmedStandardsJson ?? null
    ).length > 0
  const productExportableMet = isProductExportable(input)

  const requirements: LvdProductComplianceFileDeclarationOfConformityRequirement[] =
    [
      { key: 'manufacturerIdentity', met: manufacturerMet },
      { key: 'declarationObject', met: declarationObjectMet },
      { key: 'confirmedStandards', met: confirmedStandardsMet },
      { key: 'productExportable', met: productExportableMet },
    ]

  const unmetKeys = requirements
    .filter((requirement) => !requirement.met)
    .map((requirement) => requirement.key)
  const unmetKeysExceptManufacturer = unmetKeys.filter(
    (key) => key !== 'manufacturerIdentity'
  )

  return {
    requirements,
    allMet: unmetKeys.length === 0,
    generateReady: unmetKeysExceptManufacturer.length === 0,
    unmetKeys,
    unmetKeysExceptManufacturer,
  }
}

export function hasConfirmedLvdStandardsForSignOff(
  productFile: LvdProductComplianceFileView | null
): boolean {
  return (
    parseConfirmedLvdStandards(productFile?.confirmedStandardsJson ?? null)
      .length > 0
  )
}
